import React from "react";
import Body from "@components/typography/Body";
import { TableSearchColumn } from "@search/TableSearch";
import locale from "@bonliva-crm/locale";
import { demandSourceLabel } from "@bonliva-crm/shared";
import WithTooltip from "@components/WithTooltip";
import { ItemType } from "./utils";
import { StatusCheckbox } from "./AccommodationTravelStatus";
import { CheckBox, ErrorOutline } from "@material-ui/icons";
import { CloseBox } from "mdi-material-ui";
import RowActions from "@bookings/BookingSearch/RowActions";
import { BookingScheduleSearchStore } from "@bookings/BookingSearch/BookingScheduleSearch/BookingScheduleSearchStore";

/**
 * The ADR-0008 fields of a row, read through a narrow view rather than off
 * `BookingScheduleSearchItemFragment`.
 *
 * The fragment's type comes from `generate-graphql-client`, which runs against
 * a live API server, so it lags a schema change by one generation run. Reading
 * through this keeps the column compiling in the meantime; the shape is the
 * same one the generated fragment will have, so nothing changes when it
 * catches up.
 */
type RowProvenanceView = {
  demandSource?: string | null;
  demandSourceUrl?: string | null;
  timeReportRowSources?: string[] | null;
};

const provenanceOf = (data: unknown): RowProvenanceView => data as RowProvenanceView;

export const getColumns = (
  store: BookingScheduleSearchStore
): Array<TableSearchColumn<ItemType>> => [
  {
    label: locale.Consultant,
    dataKey: "consultant",
    vAlign: "center",
    cellRenderer: ({ rowData }) => <Body>{rowData.data.ConsultantRef.Name}</Body>
  },
  {
    label: locale.Customer,
    dataKey: "customer",
    vAlign: "center",
    cellRenderer: ({ rowData }) => (
      <div>
        <Body block>{rowData.data.OrganizationRef.Name}</Body>
        <Body block>{rowData.data.HospitalRef.Name}</Body>
        {rowData.data.DepartmentsRefs.length > 0 && (
          <Body block>{rowData.data.DepartmentsRefs.map((x) => x.Name).join(", ")}</Body>
        )}
      </div>
    )
  },
  {
    label: locale.AreaOfExpertise,
    dataKey: "aoe",
    vAlign: "center",
    cellRenderer: (data) => <Body>{data.rowData.data.AreaOfExpertise}</Body>
  },
  {
    width: 96,
    label: locale.Project,
    dataKey: "project",
    vAlign: "center",
    cellRenderer: ({ rowData }) => {
      if (!rowData.data?.ProjectId) {
        return null;
      }

      return (
        <Body title={`Projektnr: ${rowData.data?.projectNumber ?? "not found"}`}>
          {rowData.data?.projectName ?? "not found"}
        </Body>
      );
    }
  },
  {
    width: 120,
    label: locale.CallNumber,
    dataKey: "callNumber",
    vAlign: "center",
    cellRenderer: ({ rowData }) => <Body>{rowData.data.callNumber ?? ""}</Body>
  },
  {
    width: 72,
    label: "Alt",
    dataKey: "alt",
    vAlign: "center",
    style: { justifyContent: "center", textAlign: "center" },
    cellRenderer: ({ rowData }) => (
      <Body style={{ borderBottom: "1px dashed darkkhaki" }}>
        {rowData.data.ContractTypeAbbr}
      </Body>
    )
  },
  {
    width: 72,
    fitted: true,
    label: locale.ConsultantContractBooked,
    dataKey: "ConsultantContractBooked",
    style: { justifyContent: "center" },
    cellRenderer: ({ rowData }) => (
      <div style={{ display: "flex", alignItems: "center" }}>
        {rowData.data.consultantContractId ? (
          <CheckBox style={{ color: "green" }} />
        ) : (
          <CloseBox color={"error"} />
        )}
      </div>
    )
  },
  {
    width: 72,
    fitted: true,
    label: locale.CustomerContractBooked,
    dataKey: "CustomerContractBooked",
    style: { justifyContent: "center" },
    cellRenderer: ({ rowData }) => (
      <div style={{ display: "flex", alignItems: "center" }}>
        {rowData.data.customerContractId ? (
          <CheckBox style={{ color: "green" }} />
        ) : (
          <CloseBox color={"error"} />
        )}
      </div>
    )
  },
  {
    width: 72,
    fitted: true,
    label: locale.TravelBooked,
    dataKey: "travelBooked",
    style: { justifyContent: "center" },
    cellRenderer: ({ rowData }) => (
      <StatusCheckbox
        status={rowData.data.TravelStatus}
        audit={rowData.data.TravelAudit}
        bookingId={rowData.data.BookingRef.NodeID}
      />
    )
  },
  {
    width: 72,
    fitted: true,
    label: locale.Accommodation,
    dataKey: "accommodationBooked",
    style: { justifyContent: "center" },
    cellRenderer: ({ rowData }) => {
      return (
        <StatusCheckbox
          status={rowData.data.AccommodationStatus}
          audit={rowData.data.AccommodationAudit}
          bookingId={rowData.data.BookingRef.NodeID}
        />
      );
    }
  },
  {
    width: 72,
    fitted: true,
    label: locale.NPTravel,
    dataKey: "requireTravelInvoicing",
    tooltip: locale.tooltipBookingSchemaRequiredTravelInvoicing,
    style: { justifyContent: "center" },
    cellRenderer: ({ rowData }) => (
      <div style={{ display: "flex", alignItems: "center" }}>
        {rowData.data.requireTravelInvoicing && !rowData.data.isTravelInvoiced ? (
          <WithTooltip title={locale.NPRequiredIconTooltip}>
            <ErrorOutline style={{ color: "green" }} />
          </WithTooltip>
        ) : null}
        {rowData.data.isTravelInvoiced ? (
          <WithTooltip title={locale.NPTravelInvoicedIconTooltip}>
            <CheckBox style={{ color: "green" }} />
          </WithTooltip>
        ) : null}
      </div>
    )
  },
  {
    width: 120,
    fitted: true,
    label: locale.TimeReportAbbr,
    dataKey: "timeReported",
    style: { justifyContent: "center" },
    cellRenderer: ({ rowData }) => {
      return (
        <WithTooltip title={locale.TimeReported}>
          <Body block>
            {rowData.data.TimeReportStatus &&
              locale.timeReportStatus[rowData.data.TimeReportStatus]}
          </Body>
        </WithTooltip>
      );
    }
  },
  // Two columns, because they answer two different questions and collapsing
  // them would invent an answer: where the work came from, and how its hours
  // were reported (ADR-0008). A Bemlo-sourced week whose hours arrived on paper
  // reads "Bemlo" here and "Paper" next to it, which is the ordinary case.
  {
    width: 130,
    fitted: true,
    label: locale.DemandSource,
    dataKey: "demandSource",
    style: { justifyContent: "center" },
    cellRenderer: ({ rowData }) => {
      const { demandSource, demandSourceUrl } = provenanceOf(rowData.data);

      if (!demandSource) {
        return null;
      }

      // A deep link, because "which platform is this from" gets asked while
      // looking at the list — the answer has to be openable from here. Sources
      // with no addressable page render as plain text rather than a dead link.
      return demandSourceUrl ? (
        <a
          href={demandSourceUrl}
          target="_blank"
          rel="noreferrer noopener"
          onClick={(e) => e.stopPropagation()}
        >
          <Body>{demandSourceLabel(demandSource)}</Body>
        </a>
      ) : (
        <Body>{demandSourceLabel(demandSource)}</Body>
      );
    }
  },
  {
    width: 130,
    fitted: true,
    label: locale.TimereportRowSource,
    dataKey: "timeReportRowSources",
    style: { justifyContent: "center" },
    cellRenderer: ({ rowData }) => {
      const sources = provenanceOf(rowData.data).timeReportRowSources ?? [];

      // Empty means the week has no rows yet, or only rows written before
      // provenance was recorded — worth saying, since a blank cell beside a
      // week with hours in it otherwise reads as a bug.
      if (sources.length === 0) {
        return (
          <WithTooltip title={locale.TimereportRowSourceNotRecorded}>
            <Body>—</Body>
          </WithTooltip>
        );
      }

      // A week can legitimately mix channels: the consultant reports Monday in
      // the app and the rest arrives from Bemlo. Showing all of them is the
      // point — one label would hide exactly the case worth checking.
      return <Body block>{sources.join(", ")}</Body>;
    }
  },
  {
    width: 50,
    fitted: true,
    label: null,
    dataKey: "actions",
    style: { justifyContent: "center" },
    cellRenderer: ({ rowData }) => {
      return (
        <RowActions
          id={rowData.data.BookingRef.NodeID}
          onCloneBooking={store.cloneBookingForMonthlyBrake}
        />
      );
    }
  }
];
